import { AppError, ErrorClassification } from "./errors";
import type { Choice, Question } from "./types";

export const DISPLAY_CHOICE_IDS = ["A", "B", "C", "D"] as const;

export type DisplayChoiceId = (typeof DISPLAY_CHOICE_IDS)[number];

export interface DisplayChoice {
  readonly displayId: DisplayChoiceId;
  readonly choice: Choice;
}

const QUESTION_ID_HASH_MULTIPLIER = 31;
const UNSIGNED_INTEGER_SHIFT = 0;

export function getDisplayChoices(question: Question): readonly DisplayChoice[] {
  if (question.choices.length !== DISPLAY_CHOICE_IDS.length) {
    throw new AppError(
      "Assessment questions must contain exactly four choices.",
      ErrorClassification.INVARIANT_VIOLATION,
      "INVALID_DISPLAY_CHOICE_COUNT",
    );
  }

  const uniqueChoiceIds = new Set(question.choices.map((choice) => choice.id));
  const correctChoices = question.choices.filter(
    (choice) => choice.id === question.correctChoiceId,
  );
  if (
    uniqueChoiceIds.size !== DISPLAY_CHOICE_IDS.length ||
    correctChoices.length !== 1
  ) {
    throw new AppError(
      "Assessment choice identifiers and the correct answer must be unambiguous.",
      ErrorClassification.INVARIANT_VIOLATION,
      "INVALID_DISPLAY_CHOICE_SCHEMA",
    );
  }

  const correctChoice = correctChoices[0];
  if (correctChoice === undefined) {
    throw new AppError(
      "Assessment question is missing its correct choice.",
      ErrorClassification.INVARIANT_VIOLATION,
      "MISSING_DISPLAY_CORRECT_CHOICE",
    );
  }

  const correctDisplayIndex = stableDisplayIndex(question.id);
  const incorrectChoices = question.choices.filter(
    (choice) => choice.id !== question.correctChoiceId,
  );
  const orderedChoices = [
    ...incorrectChoices.slice(0, correctDisplayIndex),
    correctChoice,
    ...incorrectChoices.slice(correctDisplayIndex),
  ];

  return orderedChoices.map((choice, index) => {
    const displayId = DISPLAY_CHOICE_IDS[index];
    if (displayId === undefined) {
      throw new AppError(
        "Assessment display labels must cover every ordered choice.",
        ErrorClassification.INVARIANT_VIOLATION,
        "MISSING_DISPLAY_CHOICE_ID",
      );
    }

    return { displayId, choice };
  });
}

function stableDisplayIndex(questionId: string): number {
  if (questionId.trim().length === 0) {
    throw new AppError(
      "Assessment question identifiers must be non-empty.",
      ErrorClassification.INVARIANT_VIOLATION,
      "EMPTY_DISPLAY_QUESTION_ID",
    );
  }

  let hash = 0;
  for (const character of questionId) {
    const codePoint = character.codePointAt(0);
    if (codePoint === undefined) {
      throw new AppError(
        "Assessment question identifier contains an unreadable character.",
        ErrorClassification.INVARIANT_VIOLATION,
        "INVALID_DISPLAY_QUESTION_ID",
      );
    }

    hash = (
      Math.imul(hash, QUESTION_ID_HASH_MULTIPLIER) + codePoint
    ) >>> UNSIGNED_INTEGER_SHIFT;
  }

  return hash % DISPLAY_CHOICE_IDS.length;
}
